import { ApiResponse, ApiService } from './api.service';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';

import { Layout } from '@app/models/layout';
import { MapDataService } from './map-data.service';
import { PlaceDataService } from './place-data.service';
import { SeriesMapDataService } from '@app/services/series-map-data.service';
import { SyncStatusService } from '@app/services/sync-status.service';
import { takeUntil } from 'rxjs/operators';

export class InvalidLayoutException extends Error {
  layout: Layout = null;

  constructor(message: string, layout?: Layout) {
    super(message);
    this.layout = layout;
  }
}

@Injectable({
  providedIn: 'root',
})
export class LayoutDataService extends SyncStatusService implements OnDestroy {
  public layouts: Observable<Layout[]>;
  public tags: Observable<string[]>;

  private _loaded = false;
  private _layouts: BehaviorSubject<Layout[]>;
  private _tags: BehaviorSubject<string[]>;
  private data: {
    layouts: Layout[];
    tags: string[];
    updated: Set<string>; // missing Place and/or Map
  };
  private _destroy = new Subject<void>();
  private URL = 'layouts';

  constructor(
    private api: ApiService,
    private mapData: MapDataService,
    private placeData: PlaceDataService,
    private seriesMapData: SeriesMapDataService,
  ) {
    super();
    this.data = {
      layouts: [],
      tags: [],
      updated: new Set(),
    };
    this._layouts = new BehaviorSubject([]);
    this.layouts = this._layouts.asObservable();
    this._tags = new BehaviorSubject([]);
    this.tags = this._tags.asObservable();

    this.layouts.pipe(takeUntil(this._destroy)).subscribe((layouts) => {
      this.data.tags = [];
      layouts.map((layout) => {
        layout.tags.map((tag: string) => {
          if (this.data.tags.indexOf(tag) < 0) {
            this.data.tags.push(tag);
          }
        });
      });
      this._tags.next(Object.assign({}, this.data).tags);
    });
    // Now that layouts are being loaded we need to
    // watch for changes in Maps and Places and update
    // our layouts (and call .next()) as necessary.
    // If Map/Place is deleted delete it from layouts here and in database.
    combineLatest([
      this.mapData.maps,
      this.seriesMapData.maps,
      this.mapData.syncSuccess$,
      this.seriesMapData.syncSuccess$,
      this.syncSuccess$,
    ])
      .pipe(takeUntil(this._destroy))
      .subscribe(
        ([
          maps,
          seriesMaps,
          mapSyncSuccess,
          seriesSyncSuccess,
          layoutSyncSuccess,
        ]) => {
          // wait for all APIs to be synced before updating layouts
          if (seriesSyncSuccess && mapSyncSuccess && layoutSyncSuccess) {
            const allMaps = maps.concat(seriesMaps);

            const deleteLayouts = this.data.layouts.filter((layout) => {
              layout.map = allMaps.find((map) => layout.map_id === map.id);
              const missing = layout.map === undefined;
              if (missing) {
                console.warn('Layout will be deleted - missing map:', {
                  layoutName: layout.name,
                  layoutId: layout.id,
                  missingMapId: layout.map_id,
                });
              }
              return missing;
            });
            this._removeDeletedLayouts(deleteLayouts);
            this._publish();
          }
        },
      );

    combineLatest([
      this.placeData.places,
      this.mapData.syncSuccess$,
      this.seriesMapData.syncSuccess$,
      this.syncSuccess$,
    ])
      .pipe(takeUntil(this._destroy))
      .subscribe(
        ([places, mapSyncSuccess, seriesSyncSuccess, layoutSyncSuccess]) => {
          // wait for all APIs to be synced before updating layouts
          if (seriesSyncSuccess && mapSyncSuccess && layoutSyncSuccess) {
            const deleteLayouts = this.data.layouts.filter((layout) => {
              layout.place = places.find(
                (place) => layout.place_id === place.id,
              );
              const missing = layout.place === undefined;
              if (missing) {
                console.warn('Layout will be deleted - missing place:', {
                  layoutName: layout.name,
                  layoutId: layout.id,
                  missingPlaceId: layout.place_id,
                });
              }
              return missing;
            });
            this._removeDeletedLayouts(deleteLayouts);
            this._publish();
          }
        },
      );
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
    this._clearSyncTimeout();
  }

  layoutsExist(): boolean {
    return this.data.layouts.length > 0;
  }

  byId(id: string): Layout {
    return this.data.layouts.find((layout) => layout.id === id);
  }

  async load(force = false): Promise<Array<Layout>> {
    if (this._loaded && !force) {
      return Promise.resolve([...this.data.layouts]);
    }
    const dbLoadStart: number = new Date().getTime();
    try {
      const docs: ApiResponse = await this.api.allDocs(this.URL);
      this._lastSync = docs.until;

      this.data.layouts = docs.results.map((payload) =>
        this._payloadToInstance(payload),
      );

      this._loaded = true;
      this._publish();

      const delta = new Date().getTime() - dbLoadStart;
      const numLayouts = (this.data.layouts && this.data.layouts.length) || 0;
      console.debug('Database', delta, 'Load', 'Layouts');
      console.debug('Num Layouts', numLayouts);
    } catch (e) {
      console.error(e);
      throw e;
    }
    return this.data.layouts;
  }

  public async loadById(id: string): Promise<Layout | null> {
    return this.api.get<Layout>(this.URL, id).then(async (l) => {
      try {
        return this._payloadToInstance(l);
      } catch (e) {
        console.error(e);
      }
      return null;
    });
  }

  public getById(id: string): Layout {
    return this.data.layouts.find((obj) => obj.id === id);
  }

  public async delete(deleted: Layout): Promise<void> {
    return this.api.delete<Layout>(this.URL, deleted.id).then(() => {
      this._broadcastDelete(deleted.id);
    });
  }

  public unload(): void {
    this.data.layouts = [];
    this._publish();
  }

  public async createOrUpdate(layout: Layout): Promise<Layout> {
    let result, updated: Layout;
    // only send map/place ids not the objects to the server
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { map, place, map_id, place_id, ...payload } = layout;
    try {
      result = await this.api.put<Layout>(
        this.URL,
        layout.id,
        {
          ...layout.toJSON(),
          // changing type of map and place
          map: layout.map_id,
          place: layout.place_id,
        } as unknown as Layout,
        layout.updated,
      );

      updated = this._addUpdate(result);
      this._publish();
    } catch (e) {
      if (e.status !== 409) {
        throw e;
      }
      // 409 is a conflict, meaning the doc was updated on the server
      // reload it and rethrow exception so user can be informed.
      const obj = await this.api.get<Layout>(this.URL, layout.id);
      updated = this._addUpdate(obj);
      this._publish();
      throw e;
    }
    return updated;
  }

  protected async _getLatestFromApi(): Promise<Layout[]> {
    const docs: ApiResponse = await this.api.allDocs(this.URL, this._lastSync);
    this._lastSync = docs.until;
    return docs.results;
  }

  protected _addUpdate(payload: Layout) {
    const layout: Layout = this._payloadToInstance(payload);
    this._processChange(layout);
    return layout;
  }

  protected _publish() {
    // new object list to trigger change detection and isolate changes.
    if (!this._layouts.closed) {
      const sorted = this.data.layouts.sort((a, b) => {
        return new Date(b.created).getTime() - new Date(a.created).getTime();
      });
      this._layouts.next(sorted);
    }
  }

  private _payloadToInstance(layout: Layout): Layout {
    let map = this.mapData.getById(layout['map_id']);
    if (!map) {
      // might be a series map
      map = this.seriesMapData.byId(layout['map_id']);
    }
    const place = this.placeData.getById(layout['place_id']);
    return new Layout({
      ...(layout as Layout),
      map,
      place,
    });
  }

  private _processChange(layout: Layout): Layout {
    this.data.layouts = [
      ...this.data.layouts.filter((l) => l.id !== layout.id),
      layout,
    ];
    return layout;
  }

  private _broadcastDelete(id: string) {
    this.data.layouts = this.data.layouts.filter(
      (layouts) => layouts.id !== id,
    );
    this._publish();
  }

  private _removeDeletedLayouts(layouts: Layout[]): void {
    // LOCALLY delete layouts that don't have place or map instances anymore
    layouts.forEach((l) => this._broadcastDelete(l.id));
  }
}
